import { Link } from "react-router-dom";
import VaultLogo from "@/components/VaultLogo";

/**
 * Cookie Policy — UK PECR / EU ePrivacy compliant disclosure.
 * Lists every cookie set by JobVault with name, purpose, duration, category.
 */
export default function Cookies() {
  return (
    <div className="min-h-screen">
      <header className="border-b-[1.5px] border-[var(--brand-ink)] grid-paper">
        <div className="max-w-4xl mx-auto px-6 py-5 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="cookies-back-home">
            <VaultLogo size={24}/>
            <span className="font-display text-2xl font-semibold tracking-tight">JobVault</span>
          </Link>
          <Link to="/" className="font-mono-d text-xs text-[var(--brand-muted)] hover:text-[var(--brand-ink)]">/back</Link>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-12">
        <p className="label-tiny">/cookie policy</p>
        <h1 className="font-display text-4xl md:text-5xl tracking-tight font-semibold mt-2 mb-2">
          one cookie. that&apos;s it.
        </h1>
        <p className="text-sm text-[var(--brand-muted)] font-mono-d mb-10">
          last updated: february 2026 · UK PECR &amp; EU ePrivacy compliant
        </p>

        <section className="card-hard rounded-md p-6 bg-white mb-6">
          <h2 className="font-display text-2xl tracking-tight mb-3">tl;dr</h2>
          <p className="text-sm leading-relaxed">
            We use a single strictly-necessary cookie that keeps you logged in.
            We don&apos;t use Google Analytics, Mixpanel, Hotjar, ad-network pixels, or any third-party tracking.
            If we ever did, this page would have a consent toggle. It doesn&apos;t.
          </p>
        </section>

        <section className="mb-6">
          <h2 className="font-display text-2xl tracking-tight mb-3">what we set</h2>
          <div className="overflow-x-auto card-hard rounded-md bg-white">
            <table className="w-full text-sm">
              <thead className="bg-[var(--brand-bg)] border-b-[1.5px] border-[var(--brand-ink)]">
                <tr>
                  <th className="text-left p-3 font-mono-d label-tiny">name</th>
                  <th className="text-left p-3 font-mono-d label-tiny">purpose</th>
                  <th className="text-left p-3 font-mono-d label-tiny">duration</th>
                  <th className="text-left p-3 font-mono-d label-tiny">category</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b border-[var(--brand-bg)]">
                  <td className="p-3 font-mono-d text-xs">access_token</td>
                  <td className="p-3">Keeps you logged in. Short-lived JWT, HttpOnly + Secure.</td>
                  <td className="p-3">15 minutes</td>
                  <td className="p-3"><span className="label-tiny bg-[var(--pastel-mint)] px-2 py-0.5 rounded">strictly necessary</span></td>
                </tr>
                <tr className="border-b border-[var(--brand-bg)]">
                  <td className="p-3 font-mono-d text-xs">refresh_token</td>
                  <td className="p-3">Renews your session when access_token expires. HttpOnly + Secure.</td>
                  <td className="p-3">30 days</td>
                  <td className="p-3"><span className="label-tiny bg-[var(--pastel-mint)] px-2 py-0.5 rounded">strictly necessary</span></td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>

        <section className="mb-6">
          <h2 className="font-display text-2xl tracking-tight mb-3">localStorage / sessionStorage</h2>
          <p className="text-sm leading-relaxed mb-2">We also use small amounts of browser storage (not cookies, but worth disclosing):</p>
          <ul className="text-sm space-y-1 list-disc pl-5 leading-relaxed">
            <li><span className="font-mono-d text-xs">jv_cookie_notice_dismissed_v1</span> — remembers you closed the cookie banner.</li>
            <li><span className="font-mono-d text-xs">vault_unlocked_once</span> — skips the unlock splash on subsequent dashboard visits within the same browser session.</li>
            <li><span className="font-mono-d text-xs">jv_ai_consent_v1</span> — remembers your one-time consent to send job-ad text and CV bullets to the Vault Coach AI.</li>
          </ul>
        </section>

        <section className="mb-6">
          <h2 className="font-display text-2xl tracking-tight mb-3">third-party cookies</h2>
          <p className="text-sm leading-relaxed mb-2">When you do specific actions, these third-party cookies may be set by their own domains (not by us):</p>
          <ul className="text-sm space-y-1 list-disc pl-5 leading-relaxed">
            <li><b>Stripe</b> — sets fraud-prevention cookies on its own checkout pages when you upgrade to Premium / Unlimited. See <a className="underline" href="https://stripe.com/privacy" target="_blank" rel="noreferrer">stripe.com/privacy</a>.</li>
            <li><b>Cloudflare</b> — sets a security cookie (<span className="font-mono-d text-xs">__cf_bm</span>) to mitigate bot attacks across the jobvault.co.uk domain.</li>
          </ul>
        </section>

        <section className="card-hard rounded-md p-6 bg-[var(--pastel-yellow)] mb-12">
          <h2 className="font-display text-2xl tracking-tight mb-3">disabling cookies</h2>
          <p className="text-sm leading-relaxed">
            Strictly-necessary cookies are required for the app to work — if you block <span className="font-mono-d text-xs">access_token</span> and <span className="font-mono-d text-xs">refresh_token</span>, you won&apos;t be able to log in.
            All major browsers let you disable cookies in settings; we recommend doing so per-site rather than globally.
          </p>
        </section>

        <p className="text-xs font-mono-d text-[var(--brand-muted)] mb-10">
          See also our <Link to="/privacy" className="underline">Privacy Policy</Link> and{" "}
          <Link to="/terms" className="underline">Terms of Service</Link>.
        </p>
      </main>

      <footer className="border-t-[1.5px] border-[var(--brand-ink)] py-6 text-center font-mono-d text-xs text-[var(--brand-muted)]">
        © JobVault {new Date().getFullYear()} · made with love &amp; rage in the UK
      </footer>
    </div>
  );
}
